<script>
/**
 * 출금 신청 — 오르고 회원의 현금 출금 신청을 처리한다(본사 전용, 2026-11-01).
 *
 * 회원이 신청하면 앱 서버가 그 순간 출금액 + 수수료를 원화에서 뺀다(묶는다).
 * 직원이 회사 계좌에서 받는 계좌로 이체한 뒤 '완료' 를 누르고,
 * 보내지 않을 신청은 사유를 적어 '반려' 한다 — 반려하면 묶은 금액이 회원 원화로 돌아간다.
 * API: GET /api/v1/cash-withdraw · PUT /{id}/complete · PUT /{id}/reject
 */
import { getCashWithdrawList, putCashWithdrawComplete, putCashWithdrawReject } from '@/api/cashWithdraw.js'
import AppPagination from '@/components/common/AppPagination.vue'

const toDate = (d) => d.toISOString().substring(0, 10)

function defaultSearch() {
  const today = new Date()
  const start = new Date(today)
  start.setMonth(start.getMonth() - 3)
  return { status: 'REQUESTED', keyword: '', startDate: toDate(start), endDate: toDate(today) }
}

export default {
  name: 'CashWithdrawPage',
  components: { AppPagination },
  data() {
    return {
      search: defaultSearch(),
      statusOptions: [
        { title: '처리 대기', value: 'REQUESTED' },
        { title: '출금완료', value: 'COMPLETED' },
        { title: '출금반려', value: 'REJECTED' },
        { title: '전체', value: '' },
      ],
      items: [],
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      // 완료 · 반려 확인 창
      dialog: false,
      dialogMode: 'complete',
      target: null,
      rejectMemo: '',
      processing: false,
      errorText: '',
      snackbar: false,
      snackbarText: '',
    }
  },
  computed: {
    dialogTitle() {
      return this.dialogMode === 'complete' ? '출금 완료 처리' : '출금 반려'
    },
    canSubmit() {
      return this.dialogMode === 'complete' || this.rejectMemo.trim().length > 0
    },
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await getCashWithdrawList({
          status: this.search.status || undefined,
          keyword: this.search.keyword || undefined,
          startDate: this.search.startDate || undefined,
          endDate: this.search.endDate || undefined,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total ?? 0
        } else {
          this.items = []
          this.total = 0
        }
      } catch (e) {
        console.error('[CashWithdrawPage] fetchList', e)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = defaultSearch()
      this.page = 1
      this.fetchList()
    },
    onPageChange(p) {
      this.page = p
      this.fetchList()
    },
    onSizeChange(s) {
      this.size = s
      this.page = 1
      this.fetchList()
    },
    openDialog(mode, item) {
      this.dialogMode = mode
      this.target = item
      this.rejectMemo = ''
      this.errorText = ''
      this.dialog = true
    },
    async submit() {
      if (!this.target || !this.canSubmit || this.processing) return
      this.processing = true
      this.errorText = ''
      try {
        const id = this.target.withdrawId
        const res = this.dialogMode === 'complete'
          ? await putCashWithdrawComplete(id)
          : await putCashWithdrawReject(id, this.rejectMemo.trim())
        if (res.data?.code !== 0) {
          this.errorText = res.data?.message || '처리하지 못했습니다.'
          return
        }
        const refunded = res.data.data?.refundedAmount || 0
        this.snackbarText = this.dialogMode === 'complete'
          ? '출금 완료로 처리했습니다.'
          : `반려했습니다. ${this.won(refunded)}을 회원 원화로 되돌렸습니다.`
        this.snackbar = true
        this.dialog = false
        this.fetchList()
      } catch (e) {
        console.error('[CashWithdrawPage] submit', e)
        this.errorText = '처리하지 못했습니다. 잠시 뒤 다시 시도해 주세요.'
      } finally {
        this.processing = false
      }
    },
    won(v) {
      return v == null ? '-' : `${Number(v).toLocaleString()}원`
    },
    formatDateTime(val) {
      if (!val) return '-'
      return String(val).substring(0, 16).replace('T', ' ')
    },
    statusColor(status) {
      if (status === 'COMPLETED') return 'success'
      if (status === 'REJECTED') return 'error'
      return 'warning'
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">출금 신청</div>
        <div class="text-body-2 text-grey mt-1">
          오르고 회원의 현금 출금 신청입니다. 신청 금액과 수수료는 신청 때 회원 원화에서 이미 빠져 있습니다.
          이체를 마치면 '완료', 보내지 않을 신청은 사유를 적어 '반려' 하세요 — 반려하면 묶인 금액이 회원 원화로 돌아갑니다.
        </div>
      </div>
    </div>

    <v-card rounded="lg" elevation="1" class="mt-5 mb-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      </v-card-item>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" sm="3">
            <v-text-field v-model="search.startDate" label="신청일 시작" type="date" variant="outlined" density="compact" hide-details />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field v-model="search.endDate" label="신청일 끝" type="date" variant="outlined" density="compact" hide-details />
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="search.status"
              :items="statusOptions"
              item-title="title"
              item-value="value"
              label="상태"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="search.keyword"
              label="키워드"
              placeholder="회원명, 회원코드, 휴대폰"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              @keyup.enter="handleSearch"
            />
          </v-col>
        </v-row>
        <div class="d-flex justify-end ga-2 mt-4 border-t-thin pt-4">
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1">
      <v-card-item class="py-3">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          출금 신청 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
        </v-card-title>
      </v-card-item>

      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <v-table density="compact" class="border-t cw-table">
        <thead>
          <tr>
            <th class="text-center">번호</th>
            <th class="text-center">회원</th>
            <th class="text-right">출금액</th>
            <th class="text-right">수수료</th>
            <th class="text-center">받는 계좌</th>
            <th class="text-right">묶인 금액</th>
            <th class="text-center">상태</th>
            <th class="text-center">신청일시</th>
            <th class="text-center">처리</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in items" :key="item.withdrawId">
            <td class="text-center text-caption text-grey">{{ total - (page - 1) * size - index }}</td>
            <td class="text-center font-weight-medium">
              {{ item.userName || '-' }}
              <span class="text-caption text-grey d-block">{{ item.userCode }}</span>
            </td>
            <td class="text-right font-weight-bold">{{ won(item.amount) }}</td>
            <td class="text-right text-caption">{{ won(item.feeAmount) }}</td>
            <td class="text-center text-body-2">
              <template v-if="item.accountNo">
                {{ item.bankName || '-' }} {{ item.accountNo }}
                <span class="text-caption text-grey d-block">
                  예금주 {{ item.accountName || '-' }}
                  <v-chip v-if="item.accountFallback" size="x-small" color="warning" variant="tonal" label class="ml-1">
                    지금 계좌
                  </v-chip>
                </span>
              </template>
              <span v-else class="text-grey">등록된 출금계좌 없음</span>
            </td>
            <td class="text-right text-caption">
              {{ won(item.heldAmount) }}
              <span v-if="item.status === 'REQUESTED' && !item.heldAmount" class="text-caption text-warning d-block">묶기 전 신청</span>
            </td>
            <td class="text-center">
              <v-chip size="x-small" label variant="tonal" :color="statusColor(item.status)">{{ item.statusLabel }}</v-chip>
              <span v-if="item.processMemo" class="text-caption text-grey d-block cw-memo" :title="item.processMemo">{{ item.processMemo }}</span>
            </td>
            <td class="text-center text-caption text-grey-darken-1">
              {{ formatDateTime(item.insertDt) }}
              <span v-if="item.completeDt" class="d-block">처리 {{ formatDateTime(item.completeDt) }}</span>
            </td>
            <td class="text-center">
              <template v-if="item.status === 'REQUESTED'">
                <v-btn size="small" color="primary" variant="flat" class="mr-1" @click="openDialog('complete', item)">완료</v-btn>
                <v-btn size="small" color="error" variant="outlined" @click="openDialog('reject', item)">반려</v-btn>
              </template>
              <span v-else class="text-caption text-grey">-</span>
            </td>
          </tr>
          <tr v-if="!loading && items.length === 0">
            <td colspan="9" class="text-center text-grey py-10">
              <v-icon size="40" class="d-block mx-auto mb-2" color="grey-lighten-2">mdi-bank-transfer-out</v-icon>
              검색 결과가 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>

      <div class="px-4 pb-4">
        <AppPagination :page="page" :size="size" :total="total" @update:page="onPageChange" @update:size="onSizeChange" />
      </div>
    </v-card>

    <v-dialog v-model="dialog" max-width="440" persistent>
      <v-card rounded="lg">
        <v-card-item class="pa-5 pb-3">
          <template #prepend>
            <v-icon :color="dialogMode === 'complete' ? 'primary' : 'error'">
              {{ dialogMode === 'complete' ? 'mdi-bank-check' : 'mdi-cash-refund' }}
            </v-icon>
          </template>
          <v-card-title class="text-subtitle-1 font-weight-bold">{{ dialogTitle }}</v-card-title>
        </v-card-item>
        <v-card-text v-if="target" class="px-5 pb-3">
          <div class="mb-2">
            <span class="font-weight-bold">{{ target.userName || target.userCode }}</span> 님 ·
            <span class="font-weight-bold">{{ won(target.amount) }}</span>
          </div>
          <div class="text-body-2 mb-3">
            {{ target.bankName || '-' }} {{ target.accountNo || '' }} (예금주 {{ target.accountName || '-' }})
          </div>
          <template v-if="dialogMode === 'complete'">
            <div class="text-caption text-medium-emphasis">
              위 계좌로 이체를 마쳤을 때만 누르세요. 완료하면 되돌릴 수 없습니다.
            </div>
          </template>
          <template v-else>
            <div class="text-caption text-medium-emphasis mb-3">
              반려하면 묶인 금액 <span class="font-weight-bold">{{ won(target.heldAmount) }}</span>이 회원 원화로 돌아갑니다.
              <template v-if="!target.heldAmount">(묶기 전 신청이라 되돌릴 금액이 없습니다)</template>
            </div>
            <v-textarea
              v-model="rejectMemo"
              label="반려 사유"
              placeholder="회원 문의에 답할 때 씁니다"
              variant="outlined"
              rows="2"
              counter="500"
              maxlength="500"
              hide-details="auto"
            />
          </template>
          <v-alert v-if="errorText" type="error" variant="tonal" density="compact" class="mt-3">{{ errorText }}</v-alert>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" rounded="lg" :disabled="processing" @click="dialog = false">취소</v-btn>
          <v-btn
            :color="dialogMode === 'complete' ? 'primary' : 'error'"
            variant="flat"
            rounded="lg"
            :loading="processing"
            :disabled="!canSubmit"
            @click="submit"
          >
            {{ dialogMode === 'complete' ? '완료' : '반려' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" timeout="3000" color="success">{{ snackbarText }}</v-snackbar>
  </div>
</template>

<style scoped>
.cw-table :deep(th),
.cw-table :deep(td) {
  white-space: nowrap;
}
.cw-memo {
  max-width: 160px;
  margin: 0 auto;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
